import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Field } from '../../components/molecules/Field'
import type { ComponentPickerEntry } from '../../../viewmodels/useProductComponents'

interface Props {
  readonly open: boolean
  readonly parentName: string
  readonly picker: ReadonlyArray<ComponentPickerEntry>
  readonly onClose: () => void
  readonly onSubmit: (input: {
    componentProductId: string
    quantity: number
    sortOrder: number
  }) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function ProductComponentAddModal({
  open,
  parentName,
  picker,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [componentProductId, setComponentProductId] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<number>(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    if (componentProductId === '') {
      setError('Pick a component product')
      return
    }
    if (!Number.isInteger(quantity) || quantity < 1) {
      setError('Quantity must be a positive integer')
      return
    }
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setError('Sort order must be 0 or greater')
      return
    }
    setSubmitting(true)
    const res = await onSubmit({ componentProductId, quantity, sortOrder })
    setSubmitting(false)
    if (!res.ok) setError(res.message)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add component to ${parentName}`}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        <Field
          label="Component product"
          helper="The sub-product that's part of the bundle. Products that already have their own components are disabled (depth-1 limit)."
          error={null}
        >
          <Select
            value={componentProductId}
            onChange={(e) => setComponentProductId(e.target.value)}
          >
            <option value="">— Select a product —</option>
            {picker.map((p) => (
              <option key={p.id} value={p.id} disabled={p.disabled}>
                {p.name}
                {p.disabled ? ' (has its own components)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Quantity"
          helper="How many units of this component go into one bundle."
          error={null}
        >
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={String(quantity)}
            onChange={(e) => {
              const raw = e.target.value.trim()
              if (raw === '') {
                setQuantity(1)
                return
              }
              const n = Number(raw)
              if (Number.isInteger(n) && n >= 1) setQuantity(n)
            }}
          />
        </Field>

        <Field
          label="Sort order"
          helper="Display order. Lower = earlier. Leave 0 if order doesn't matter."
          error={null}
        >
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={String(sortOrder)}
            onChange={(e) => {
              const raw = e.target.value.trim()
              if (raw === '') {
                setSortOrder(0)
                return
              }
              const n = Number(raw)
              if (Number.isInteger(n) && n >= 0) setSortOrder(n)
            }}
          />
        </Field>
      </div>
    </Modal>
  )
}
